import React, {
  createContext, useContext, useEffect, useMemo, useState
} from 'react';

import {
  BarcodeCapture,
  barcodeCaptureLoader,
  BarcodeCaptureOverlay,
  BarcodeCaptureSettings,
  Symbology,
} from 'scandit-web-datacapture-barcode';

import {
  Camera,
  CameraSwitchControl,
  configure,
  DataCaptureContext,
  DataCaptureView,
  FrameSourceState,
  LaserlineViewfinder,
  Color,
} from 'scandit-web-datacapture-core';
import * as SDCCore from 'scandit-web-datacapture-core';
import { SCANDIT_KEY } from '../../../../common/config';

export function createSDKFacade() {
  let context;
  let view;
  let camera;
  let settings;
  let barcodeCapture;
  let overlay;
  let host;
  let barcodeCaptureListener;
  let previousListener;

  return {
    async initialize() {
      // There is a Scandit sample license key set below here.
      // This license key is enabled for sample evaluation only.
      // If you want to build your own application, get your license key by signing up for a trial at https://ssl.scandit.com/dashboard/sign-up?p=test
      // The library location option represents the location of the wasm file,
      // which will be fetched asynchronously.
      await configure({
        libraryLocation: '/assets/js/scandit-sdk@6.22.2/engine/',
        licenseKey: SCANDIT_KEY,
        moduleLoaders: [barcodeCaptureLoader()],
      });

      context = await DataCaptureContext.create();
      view = await DataCaptureView.forContext(context);
      settings = new BarcodeCaptureSettings();
      settings.enableSymbologies([
        Symbology.Code128,
        Symbology.QR,
        Symbology.DataMatrix,
        Symbology.Aztec,
        Symbology.Code39,
      ]);
      const size = new SDCCore.SizeWithUnit(
        new SDCCore.NumberWithUnit(0.9, SDCCore.MeasureUnit.Fraction),
        new SDCCore.NumberWithUnit(0.1, SDCCore.MeasureUnit.Fraction),
      );
      settings.locationSelection = SDCCore.RectangularLocationSelection.withSize(size);

      barcodeCapture = await BarcodeCapture.forContext(context, settings);
      await barcodeCapture.setEnabled(false);

      overlay = await BarcodeCaptureOverlay.withBarcodeCaptureForView(barcodeCapture, view);
      const viewfinder = new LaserlineViewfinder();
      viewfinder.enabledColor = Color.fromHex('#eda340');
      viewfinder.disabledColor = Color.fromHex('#39c1cc');
      await overlay.setViewfinder(viewfinder);
      await view.addOverlay(overlay);

      camera = Camera.default;
      await camera.applySettings(BarcodeCapture.recommendedCameraSettings);
      await context.setFrameSource(camera);

      // NOTE:
      // The following is a workaround to keep the scanner working correctly with React.
      // The DataCaptureView requires the host element to remain the same throughout its lifecycle.
      // Unfortunately, between re-renders, React doesn't keep the same nodes alive,
      // but creates new ones each time.
      // This means that, between re-renders, the DataCaptureView might
      // stop rendering overlays, viewfinders etc...
      // To fix this, we connect the DataCaptureView to a hidden element,
      // then append it to a React component.
      // This allows us to keep the node alive, and the
      // DataCaptureView rendering correctly.
      // When mounting the scanner component, we show the hidden node,
      // then hide it when unmounting the scanner.
      // See also the `connectToElement` and `detachFromElement` facade methods for further context.
      host = document.createElement('div');
      host.style.display = 'none';
      host.style.width = '100%';
      host.style.height = '100%';
      document.body.append(host);
      view.connectToElement(host);
      view.addControl(new CameraSwitchControl());
    },
    async cleanup() {
      await camera.switchToDesiredState(FrameSourceState.Off);
      await context?.dispose();
      await context?.removeAllModes();
      await view.removeOverlay(overlay);
      barcodeCapture.removeListener(barcodeCaptureListener);
      view.detachFromElement();
    },
    connectToElement(element) {
      host.style.display = 'block';
      element.append(host);
    },
    setPointOfInterest(clientHeight) {
      if (!clientHeight) {
        view.pointOfInterest = new SDCCore.PointWithUnit(
          new SDCCore.NumberWithUnit(0.5, SDCCore.MeasureUnit.Fraction),
          new SDCCore.NumberWithUnit(0.5, SDCCore.MeasureUnit.Fraction)
        );
        return;
      }
      view.pointOfInterest = new SDCCore.PointWithUnit(
        new SDCCore.NumberWithUnit(0.5, SDCCore.MeasureUnit.Fraction),
        new SDCCore.NumberWithUnit(clientHeight / 2, SDCCore.MeasureUnit.Pixel)
      );
    },
    detachFromElement() {
      host.style.display = 'none';
      document.body.append(host);
    },
    async enableCamera(enabled) {
      camera = context?.frameSource;
      await camera.switchToDesiredState(enabled ? FrameSourceState.On : FrameSourceState.Off);
    },
    async enableScanning(enabled) {
      await barcodeCapture.setEnabled(enabled);
    },
    async enableSymbology(symbology, enabled) {
      settings.enableSymbology(symbology, enabled);
      await barcodeCapture.applySettings(settings);
    },
    onScan(callback) {
      if (previousListener) {
        barcodeCapture.removeListener(previousListener);
      }
      barcodeCaptureListener = {
        didScan: callback,
      };
      previousListener = barcodeCaptureListener;
      barcodeCapture.addListener(barcodeCaptureListener);
    },
    getEnabledSymbologies() {
      return settings.enabledSymbologies;
    },
  };
}

export const sdk = createSDKFacade();

export const SDKContext = createContext({
  loading: false,
  sdk,
});

export default function SDKProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const providerValue = useMemo(() => ({ loading, sdk }), [loading]);

  useEffect(() => {
    async function start() {
      await sdk.initialize();
      setLoading(false);
    }
    start();
    return () => {
      sdk.cleanup();
    };
  }, []);

  return <SDKContext.Provider value={providerValue}>{children}</SDKContext.Provider>;
}

export function useSDK() {
  return useContext(SDKContext);
}
